import {FC} from 'react';
import {MRT_ColumnDef} from 'material-react-table';

import {
  EventLog,
  EventResult,
  EventStatus,
  formatEventAuditActor,
  formatEventResult,
  formatEventStatus,
  useGetEventLogs,
} from '@entities/event';
import {MrTable} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {PaginationState} from '@shared/types';
import {formatInTz} from '@shared/utils';

const formatNullableEventStatus = (status: EventStatus | null): string =>
  status && status !== EventStatus.null ? formatEventStatus(status) : '-';

const formatNullableEventResult = (result: EventResult | null): string =>
  result && result !== EventResult.null ? formatEventResult(result) : '-';

const columns: MRT_ColumnDef<EventLog>[] = [
  {
    accessorKey: 'action',
    header: 'Action',
    size: 220,
  },
  {
    accessorKey: 'timestamp',
    header: 'Date and time',
    Cell: ({row}) => formatInTz(row.original.timestamp, DATETIME_FORMAT),
    size: 180,
  },
  {
    accessorKey: 'reason',
    header: 'Reason',
    grow: true,
  },
  {
    accessorKey: 'actorType',
    header: 'Actor type',
    Cell: ({row}) => formatEventAuditActor(row.original.actorType),
    size: 160,
  },
  {
    accessorKey: 'actorId',
    header: 'Actor ID',
    Cell: ({row}) => row.original.actorId ?? '-',
    size: 260,
  },
  {
    accessorKey: 'statusBefore',
    header: 'Status before',
    Cell: ({row}) => formatNullableEventStatus(row.original.statusBefore),
    size: 180,
  },
  {
    accessorKey: 'statusAfter',
    header: 'Status after',
    Cell: ({row}) => formatNullableEventStatus(row.original.statusAfter),
    size: 180,
  },
  {
    accessorKey: 'resultBefore',
    header: 'Result before',
    Cell: ({row}) => formatNullableEventResult(row.original.resultBefore),
    size: 180,
  },
  {
    accessorKey: 'resultAfter',
    header: 'Result after',
    Cell: ({row}) => formatNullableEventResult(row.original.resultAfter),
    size: 180,
  },
];

interface Props {
  eventSlug: string;
}

export const EventLogsTab: FC<Props> = ({eventSlug}) => {
  const {urlState, setUrl} = useUrlState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const {data, isPending, error} = useGetEventLogs(
    {
      eventSlug,
      page: urlState.pageIndex + 1,
      pageSize: urlState.pageSize,
    },
    {
      enabled: !!eventSlug,
    },
  );

  return (
    <MrTable
      columns={columns}
      data={data?.items ?? []}
      error={error}
      enablePagination
      manualPagination
      onPaginationChange={setUrl}
      rowCount={data?.total ?? 0}
      state={{isLoading: isPending, pagination: urlState}}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
